import apiClient from "./api";
import userService from "./userService";
import { reactive } from "vue";

/**
 * Il secondo fattore e la sicurezza del proprio conto.
 *
 * <h2>Lo stato condiviso, e perche' serve</h2>
 * `statoAccesso` e' reattivo e lo leggono in due la guardia del router e la barra in alto: la
 * guardia deve sapere, PRIMA di lasciar passare, se il secondo fattore e' ancora da fare. Se lo
 * chiedesse al server a ogni navigazione servirebbe una chiamata per ogni clic; se non lo
 * chiedesse affatto, ogni pagina si aprirebbe vuota con dodici 403 dentro.
 *
 * Il flag e' una comodita', non una difesa: chi lo mette a `false` da console non ottiene
 * niente, perche' ogni chiamata resta negata dal filtro lato server. E' la stessa divisione di
 * `permessiService`, dove le tabelle servono a nascondere i pulsanti e a decidere e' il server.
 */
const statoAccesso = reactive({
  /** Vero quando la persona e' entrata con la password ma non ha ancora dato il codice. */
  richiesto: false,
  /** `EMAIL` o `TOTP`. Utile solo per il testo a schermo. */
  fattore: null,
  /** L'indirizzo a cui e' stato mandato, offuscato dal server. */
  indirizzo: null,
  /** Quanti secondi vive il codice. */
  scadeInSecondi: 0,
  /** Falso se il codice non e' stato mandato davvero (limitatore, o posta rotta). */
  codicePartito: true,
  /** Vero quando il giro di apertura e' stato fatto almeno una volta in questa scheda. */
  chiesto: false,
});

/** Dove il browser conserva il gettone del dispositivo fidato. */
const CHIAVE_DISPOSITIVO = "zoelia_dispositivo";

/**
 * Un'etichetta leggibile per questo dispositivo.
 *
 * Si ricava dallo user agent, che e' falsificabile — e non importa: l'etichetta non entra in
 * nessuna decisione, serve solo a far riconoscere la riga a chi guarda l'elenco nel profilo.
 * Chi decide e' il gettone.
 */
function etichettaDispositivo() {
  // Nell'app lo user agent e' quello di una WebView generica, e l'etichetta verrebbe «Chrome su
  // Android»: nell'elenco del profilo non si distinguerebbe l'app dal browser dello stesso
  // telefono, che sono due dispositivi fidati diversi con due gettoni diversi.
  if (process.env.VUE_APP_PIATTAFORMA === "mobile") {
    return /iPhone|iPad/.test(navigator.userAgent || "") ? "App Zoelia su iOS" : "App Zoelia su Android";
  }
  const ua = navigator.userAgent || "";
  const browser = /Edg\//.test(ua) ? "Edge"
    : /OPR\//.test(ua) ? "Opera"
      : /Chrome\//.test(ua) ? "Chrome"
        : /Firefox\//.test(ua) ? "Firefox"
          : /Safari\//.test(ua) ? "Safari" : "Browser";
  const sistema = /Windows/.test(ua) ? "Windows"
    : /Android/.test(ua) ? "Android"
      : /iPhone|iPad/.test(ua) ? "iOS"
        : /Mac OS X/.test(ua) ? "macOS"
          : /Linux/.test(ua) ? "Linux" : "sistema sconosciuto";
  return `${browser} su ${sistema}`;
}

function gettoneDispositivo() {
  try {
    return localStorage.getItem(CHIAVE_DISPOSITIVO);
  } catch (e) {
    // Finestra privata, o dati del sito bloccati: si fa finta di non avere nessun dispositivo
    // fidato, che e' il ripiego prudente — si chiede il codice.
    return null;
  }
}

function salvaGettone(gettone) {
  if (!gettone) return;
  try {
    localStorage.setItem(CHIAVE_DISPOSITIVO, gettone);
  } catch (e) {
    // Non si puo' ricordare il dispositivo: il codice si richiedera' al prossimo accesso. Non
    // e' un errore da mostrare, e' una comodita' che non c'e'.
  }
}

export const sicurezzaService = {
  statoAccesso,
  etichettaDispositivo,

  /**
   * Apre la sessione: chiede al server se il secondo fattore serve, e se serve lo fa mandare.
   *
   * Si chiama subito dopo aver ottenuto il token — dalla pagina d'accesso e, per le sessioni
   * ripristinate da disco, all'avvio dell'applicazione. Passa il gettone del dispositivo, se
   * c'e': chi arriva da un dispositivo gia' fidato non riceve nessuna posta.
   */
  async apriSessione() {
    try {
      const { data } = await apiClient.post("/account/2fa/sessione", {
        gettoneDispositivo: gettoneDispositivo(),
        etichettaDispositivo: etichettaDispositivo(),
      }, { skipToast: true });
      const d = data?.data || {};
      statoAccesso.richiesto = !!d.richiesto;
      statoAccesso.fattore = d.fattore || null;
      statoAccesso.indirizzo = d.indirizzo || null;
      statoAccesso.scadeInSecondi = d.scadeInSecondi || 0;
      // Falso quando il codice non e' partito davvero: il limitatore l'ha fermato, o la posta e'
      // rotta. La schermata lo deve dire invece di mostrare un conto alla rovescia.
      statoAccesso.codicePartito = d.codicePartito !== false;
      statoAccesso.chiesto = true;
      return statoAccesso.richiesto;
    } catch (e) {
      // Se non si riesce a sapere, si presume che serva: il ripiego prudente e' chiedere il
      // codice, non lasciar passare. Al peggio la persona vede una schermata in piu'.
      statoAccesso.chiesto = true;
      statoAccesso.richiesto = true;
      return true;
    }
  },

  /** Rimanda il codice. */
  async rimanda() {
    const { data } = await apiClient.post("/account/2fa/rimanda", {}, { skipToast: true });
    return data?.data || { partito: false };
  },

  /**
   * Verifica il codice. Se si e' chiesto di ricordare il dispositivo, il gettone che torna si
   * conserva qui: non lo si potra' rileggere mai piu' dal server, perche' la' ne resta solo
   * l'impronta.
   */
  async verifica(codice, ricordaDispositivo) {
    const { data } = await apiClient.post("/account/2fa/verifica", {
      codice,
      ricordaDispositivo: !!ricordaDispositivo,
      etichettaDispositivo: etichettaDispositivo(),
    }, { skipToast: true });
    const d = data?.data || {};
    if (d.gettoneDispositivo) {
      salvaGettone(d.gettoneDispositivo);
    }
    statoAccesso.richiesto = false;
    // La cache del profilo va svuotata: finche' il fattore era in sospeso, /account/me
    // rispondeva RIDOTTO — senza spazi, senza ruolo, senza funzioni — e userService quella
    // risposta l'ha messa in cache. Tenendola, l'applicazione resterebbe senza ruolo per tutta
    // la sessione: menu vuoto e guardie che rimbalzano.
    userService.clearCache();
    return d;
  },

  /** Lo stato della sicurezza, per la scheda del profilo. */
  async stato() {
    const { data } = await apiClient.get("/account/sicurezza");
    return data?.data || {};
  },

  /** Comincia la configurazione dell'app: torna segreto e URI per il QR. */
  async avviaApp() {
    const { data } = await apiClient.post("/account/sicurezza/totp/avvia", {}, { skipToast: true });
    return data?.data || {};
  },

  /** Conferma l'app col primo codice. Da qui in poi il codice lo pretende Keycloak. */
  async confermaApp(segreto, codice) {
    const { data } = await apiClient.post("/account/sicurezza/totp/conferma",
      { segreto, codice }, { skipToast: true });
    return data?.data || {};
  },

  /** Togli l'app: si torna al codice per posta. */
  async togliApp() {
    const { data } = await apiClient.delete("/account/sicurezza/totp");
    return data?.data || {};
  },

  async dispositivi() {
    const { data } = await apiClient.get("/account/sicurezza/dispositivi");
    return data?.data || [];
  },

  async revocaDispositivo(id) {
    await apiClient.delete(`/account/sicurezza/dispositivi/${id}`);
  },

  /** Revoca tutti i dispositivi e chiude le sessioni. Compreso quello da cui si sta guardando. */
  async revocaTuttiIDispositivi() {
    const { data } = await apiClient.delete("/account/sicurezza/dispositivi");
    try {
      localStorage.removeItem(CHIAVE_DISPOSITIVO);
    } catch (e) { /* niente da dimenticare */ }
    return data?.data || {};
  },

  async aggiornaNome(nome, cognome) {
    await apiClient.put("/account/sicurezza/nome", { nome, cognome });
  },

  // ---- prima di essere entrati -----------------------------------------------------------

  /**
   * Quale secondo fattore chiedere a questo indirizzo.
   *
   * Serve alla pagina d'accesso: chi usa l'app deve mandare il codice nella stessa richiesta
   * della password. A un indirizzo che non esiste il server risponde `EMAIL`, quindi da qui non
   * si scopre se un account c'e'.
   */
  async fattoreDi(email) {
    try {
      const { data } = await apiClient.post("/public/accesso/fattore", { email },
        { skipToast: true });
      return data?.data?.fattore || "EMAIL";
    } catch (e) {
      // Nel dubbio si presume la posta: e' il predefinito, e non chiede niente a chi entra.
      return "EMAIL";
    }
  },

  async mandaCodiceRecupero(email) {
    await apiClient.post("/public/password/codice", { email }, { skipToast: true });
  },

  async reimpostaPassword(email, codice, nuovaPassword) {
    const { data } = await apiClient.post("/public/password/reimposta",
      { email, codice, nuovaPassword }, { skipToast: true });
    return data?.data || {};
  },
};

export default sicurezzaService;
